import React, { useEffect, useState } from 'react'
import { useSessionCleanup, useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import QRCode from 'qrcode'
import { useConfigStore } from '@renderer/stores/configState'
import { resolveGallerySessionUrl } from '@renderer/utils/galleryUrlUtils'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { useTranslation } from 'react-i18next'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

const ResultPage: React.FC = () => {
  const { t } = useTranslation()
  const { gallery, id, setSessionState, uploadSessionState, mode } = useSessionStateStore()
  const sessionCleanup = useSessionCleanup()
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.result
  const [stripPreview, setStripPreview] = useState('')
  const [videoPreview, setVideoPreview] = useState('')
  const [qrCodeImage, setQrCodeImage] = useState('')
  const isPotrait =
    (globalConfig?.screenSize.height || 1080) > (globalConfig?.screenSize.width || 1920)

  // Initialize countdown hook
  const resultCountdown = useCountdown(config?.settings.resultTimeout || 120)

  // Initial setup effect
  useEffect(() => {
    resultCountdown.setOnComplete(() => {
      // Log countdown timeout
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'result',
              type: 'interaction',
              action: 'timeout',
              details: {
                type: 'result',
                duration: config?.settings.resultTimeout || 120
              },
              timestamp: new Date()
            }
          ]
        }
      })
      sessionCleanup()
    })

    resultCountdown.start()

    // Check for any ongoing video processing
    const checkVideoProcessing = async (): Promise<void> => {
      const videoProcessingId = gallery.capturedItems.find(
        (item) => item.id?.includes('video-') && backgroundVideoProcessor.isTaskProcessing(item.id)
      )?.id

      if (videoProcessingId) {
        console.log('Waiting for video processing to complete:', videoProcessingId)
        // Wait for video processing to complete
        await new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            if (!backgroundVideoProcessor.isTaskProcessing(videoProcessingId)) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 1000)
        })
      }
    }

    const setupResult = async (): Promise<void> => {
      await checkVideoProcessing()

      if (gallery.capturedItems && gallery.capturedItems.length > 0) {
        if (mode === 'record') {
          // Find the video URL
          const videoItem = gallery.capturedItems.find((item) => item.videoUrl)
          setVideoPreview(videoItem?.videoUrl || '')
        } else {
          // Find the strip URL
          const stripItem = gallery.capturedItems.find((item) =>
            item.id?.includes('dreambooth-strip')
          )
          setStripPreview(stripItem?.stripUrl || '')
        }
      }

      setSessionState({
        timeline: {
          isCompleted: true,
          activityLogs: [
            {
              page: 'result',
              type: 'session',
              action: 'complete',
              details: {
                stripUrl: mode === 'record' ? undefined : stripPreview,
                videoUrl: mode === 'record' ? videoPreview : undefined,
                sessionId: id,
                endTime: new Date()
              },
              timestamp: new Date()
            }
          ]
        }
      })

      // Generate QR code
      const galleryTarget = resolveGallerySessionUrl(id, config?.settings)
      QRCode.toDataURL(galleryTarget, {
        margin: 0,
        width: 300
      }).then(setQrCodeImage)

      const completeSession = async (): Promise<void> => {
        uploadSessionState()
      }

      completeSession()
    }

    setupResult()

    return (): void => {
      resultCountdown.stop()
    }
  }, [])

  // Call all hooks at the top level
  const cachedResultBg = useCachedBackgroundImage('/assets/photophoria/photophoria-result.webp')

  return (
    <div
      className="w-full h-full relative bg-cover bg-center"
      data-page="result"
      style={{
        backgroundImage: `url(${cachedResultBg})`
      }}
    >
      {/* Preview Section */}
      <div className="absolute top-[600px] left-[440px] -translate-y-1/2">
        {mode === 'record' && videoPreview ? (
          <div
            className={`${isPotrait ? 'h-[70vh] max-h-[600px]' : 'h-[80vh] max-h-[800px]'} flex items-center justify-center`}
          >
            <video
              src={videoPreview}
              controls
              className="h-full w-auto object-contain drop-shadow-2xl rounded-lg"
            />
          </div>
        ) : stripPreview ? (
          <img
            className={`${isPotrait ? 'h-[50vh] max-h-[600px]' : 'h-[80vh] max-h-[1000px]'} w-auto object-contain drop-shadow-2xl`}
            src={stripPreview || ''}
            alt={t('result.photoStripAlt', 'Photo Strip')}
          />
        ) : null}
      </div>

      {/* QR Code and Session ID Section */}
      <div className="absolute top-[420px] right-[290px] -translate-y-1/2 flex flex-col items-center gap-4">
        <div className="bg-white p-8 rounded-3xl shadow-2xl ring-1 ring-carbon/10 flex items-center justify-center">
          {qrCodeImage ? (
            <img
              className={`${isPotrait ? 'w-[180px] h-[180px]' : 'w-[250px] h-[250px]'}`}
              src={qrCodeImage}
              alt={t('result.qrCodeAlt', 'QR Code')}
            />
          ) : (
            <div
              className={`${isPotrait ? 'w-[180px] h-[180px]' : 'w-[250px] h-[250px]'} flex items-center justify-center`}
            >
              <svg
                className={`${isPotrait ? 'w-16 h-16' : 'w-20 h-20'} animate-spin text-carbon/40`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}
        </div>
      </div>
      <div className="absolute text-center bottom-[290px] right-[380px]">
        <p className={`text-carbon font-bold ${isPotrait ? 'text-2xl' : 'text-3xl'} font-mono`}>
          {id}
        </p>
      </div>
    </div>
  )
}

export default ResultPage
